import React, { useState, useRef } from 'react';
import { 
    Box, 
    TextField, 
    InputAdornment, 
    Paper, 
    CircularProgress,
    Snackbar,
    Alert,
    useMediaQuery,
    Autocomplete,
    Typography,
    Avatar,
    Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { searchEvents, getTypeaheadSuggestions, SearchEventsResponse, EventResponse, TypeaheadSuggestion } from '../services/eventService';
import { format } from 'date-fns';
import { DEFAULT_TIMEZONE } from '../config';

interface SearchBarProps {
    onSearchResults?: (results: SearchEventsResponse) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearchResults }) => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState<TypeaheadSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchResults, setSearchResults] = useState<SearchEventsResponse | null>(null);
    const autocompleteRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const fetchSuggestions = async (query: string) => {
        try {
            const response = await getTypeaheadSuggestions(query);
            if (Array.isArray(response)) {
                setSuggestions(response);
            }
        } catch (err) {
            console.error('Error fetching suggestions:', err);
            setSuggestions([]);
        }
    };

    const handleSearchSubmit = async (event?: React.FormEvent, queryOverride?: string) => {
        if (event) {
            event.preventDefault();
        }

        // Use queryOverride if provided (for debounced calls), otherwise use current state
        const queryToUse = queryOverride !== undefined ? queryOverride : searchQuery;

        if (!queryToUse.trim()) {
            setError('Please enter a search term');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('Searching for:', queryToUse);
            const response = await searchEvents({ query: queryToUse });
            console.log('Raw API response:', response);

            if (!response || typeof response !== 'object') {
                throw new Error('Invalid response format');
            }

            if (!Array.isArray(response.events)) {
                console.error('Events is not an array:', response.events);
                throw new Error('Invalid events data format');
            }

            setSearchResults(response);
            if (onSearchResults) {
                onSearchResults(response);
            }
        } catch (err) {
            console.error('Search error:', err);
            setError(err instanceof Error ? err.message : 'An error occurred during search');
            setSearchResults(null);
        } finally {
            setLoading(false);
        }
    };


    const handleCloseError = () => {
        setError(null);
    };


    const handleEventClick = (event: EventResponse) => {
        setSearchResults(null);
        setSearchQuery('');
        setSuggestions([]);
        navigate(`/events/${event.id}`);
    };

    return (
        <Box sx={{ 
            width: '100%', 
            maxWidth: 800, 
            mx: 'auto',
            py: isMobile ? 2 : 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
        }}>
            <Paper elevation={1} 
                sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    position: 'relative', 
                    borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0' : '8px',
                    overflow: 'hidden'
                }}
            >
                <form onSubmit={handleSearchSubmit}>
                    <Box sx={{ position: 'relative' }} ref={autocompleteRef}>
                        <Autocomplete
                            freeSolo
                            options={suggestions}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.title}
                            inputValue={searchQuery}
                            onInputChange={(event, newInputValue, reason) => {
                                setSearchQuery(newInputValue);
                                if (newInputValue.length >= 2 && reason !== 'reset') {
                                    fetchSuggestions(newInputValue);
                                    // Clear previous timeout
                                    if (searchTimeoutRef.current) {
                                        clearTimeout(searchTimeoutRef.current);
                                    }
                                    // Debounce search for results
                                    // Pass newInputValue directly to avoid closure stale state issue
                                    searchTimeoutRef.current = setTimeout(() => {
                                        if (newInputValue.trim()) {
                                            handleSearchSubmit(undefined, newInputValue);
                                        }
                                    }, 500);
                                } else if (newInputValue.length < 2) {
                                    if (searchTimeoutRef.current) {
                                        clearTimeout(searchTimeoutRef.current);
                                    }
                                    setSuggestions([]);
                                    setSearchResults(null);
                                }
                            }}
                            onChange={(event, newValue) => {
                                if (newValue && typeof newValue !== 'string') {
                                    setSearchQuery(newValue.title);
                                    handleSearchSubmit();
                                }
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    const target = event.target as HTMLInputElement;
                                    if (target.tagName === 'INPUT' && searchQuery.trim()) {
                                        event.preventDefault();
                                        handleSearchSubmit();
                                    }
                                }
                            }}
                            loading={loading}
                            size="small"
                            open={Boolean((suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) && searchQuery.length >= 2)}
                            sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                    '& fieldset': {
                                        borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                    },
                                    '&.Mui-focused': {
                                        borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                        '& fieldset': {
                                            borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                        }
                                    },
                                    '&:hover': {
                                        '& fieldset': {
                                            borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                        }
                                    }
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                    borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                },
                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                    borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                    borderWidth: '1px',
                                    borderBottom: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                                    borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                },
                                '& .MuiAutocomplete-inputRoot': {
                                    borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                },
                                '& .MuiInputBase-root': {
                                    borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                    '& fieldset': {
                                        borderRadius: (suggestions.length > 0 || (searchResults && searchResults.events && searchResults.events.length > 0)) ? '8px 8px 0 0 !important' : '8px !important',
                                    }
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search for events..."
                                    InputProps={{
                                        ...params.InputProps,
                                startAdornment: (
                                            <>
                                    <InputAdornment position="start">
                                                    <SearchIcon />
                                    </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                ),
                                        endAdornment: (
                                            <>
                                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                ),
                            }}
                        />
                            )}
                            PaperComponent={(props) => (
                                <Paper 
                                    {...props} 
                                    elevation={4}
                                    sx={{
                                        borderRadius: '0 0 4px 4px',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderTop: 'none',
                                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                        mt: 0
                                    }}
                                />
                            )}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                                offset: [0, 0],
                                    },
                                },
                                    ],
                                },
                            }}
                            ListboxComponent={(props) => (
                                <Box component="ul" {...props} sx={{ maxHeight: '400px', overflow: 'auto', p: 0, m: 0 }}>
                                    {suggestions.length > 0 && (
                                        <>
                                            <Box sx={{ 
                                                p: 1.5, 
                                                pb: 1,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                position: 'sticky',
                                                top: 0,
                                                backgroundColor: 'background.paper',
                                                zIndex: 1,
                                                borderBottom: '1px solid',
                                                borderColor: 'divider'
                                            }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Suggestions
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: 'primary.main', 
                                                        cursor: 'pointer',
                                                        '&:hover': { textDecoration: 'underline' }
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSuggestions([]);
                                                    }}
                                                >
                                                    Clear all
                                                </Typography>
                                            </Box>
                                            {suggestions.map((suggestion, index) => (
                                                <Box
                                                    component="li"
                                                    key={`${suggestion.id}-${index}`}
                                                    onClick={() => {
                                                        setSearchQuery(suggestion.title);
                                                        handleSearchSubmit();
                                                    }}
                                                    sx={{
                                                        py: 1.5,
                                                        px: 2,
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        '&:hover': {
                                                            backgroundColor: 'action.hover'
                                                        },
                                                        borderBottom: index < suggestions.length - 1 || (searchResults && searchResults.events && searchResults.events.length > 0) ? '1px solid' : 'none',
                                                        borderColor: 'divider'
                                                    }}
                                                >
                                                    <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                                    <Typography variant="body1">
                                                        {suggestion.title}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </>
                                    )}
                                    {searchResults && searchResults.events && searchResults.events.length > 0 && (
                                        <>
                                            {suggestions.length > 0 && (
                                                <Box sx={{ 
                                                    p: 1.5, 
                                                    pb: 1,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    position: 'sticky',
                                                    top: suggestions.length > 0 ? '60px' : 0,
                                                    backgroundColor: 'background.paper',
                                                    zIndex: 1,
                                                    borderTop: suggestions.length > 0 ? '1px solid' : 'none',
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider'
                                                }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        Search Results ({searchResults.total || 0})
                                                    </Typography>
                                                </Box>
                                            )}
                                            {!suggestions.length && (
                                                <Box sx={{ 
                                                    p: 1.5, 
                                                    pb: 1,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    position: 'sticky',
                                                    top: 0,
                                                    backgroundColor: 'background.paper',
                                                    zIndex: 1,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider'
                                                }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        Search Results ({searchResults.total || 0})
                                                    </Typography>
                                                </Box>
                                            )}
                                            {searchResults.events.map((event, index) => (
                                                <Box
                                                    component="li"
                                                    key={event.id}
                                                    onClick={() => handleEventClick(event)}
                                            sx={{ 
                                                        py: 1.5,
                                                        px: 2,
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 2,
                                                        '&:hover': {
                                                            backgroundColor: 'action.hover'
                                                        },
                                                        borderBottom: index < searchResults.events.length - 1 ? '1px solid' : 'none',
                                                        borderColor: 'divider'
                                            }}
                                        >
                                                    <Avatar 
                                                        src={event.imageUrl || undefined}
                                                        alt={event.title}
                                                        variant="rounded"
                                                        sx={{ 
                                                            width: 56, 
                                                            height: 56,
                                                            flexShrink: 0,
                                                            alignSelf: 'flex-start'
                                                        }}
                                                    >
                                                        {!event.imageUrl && event.title[0]}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                            <Typography 
                                                                variant="subtitle1" 
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    flex: 1,
                                                                    mr: 1
                                                                }}
                                                            >
                                                                {event.title}
                                                            </Typography>
                                                            <Chip 
                                                                label={event.sellingPrice === 0 ? 'FREE' : `€${event.sellingPrice}`} 
                                                                color="primary" 
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 500,
                                                                    height: '24px'
                                                                }}
                                                            />
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            {format(new Date(event.startTime), 'MMM d, yyyy')} {new Date(event.startTime).toLocaleTimeString('en-US', { timeZone: DEFAULT_TIMEZONE, hour: 'numeric', minute: '2-digit', hour12: true }).replace(/am/g, 'AM').replace(/pm/g, 'PM')}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            {event.venue}
                                                        </Typography>
                                                        {event.category && (
                                                            <Chip 
                                                                label={event.category.replace('_', ' ')} 
                                                                size="small" 
                                                                sx={{ mt: 0.5 }}
                                        />
                                    )}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </>
                                    )}
                                </Box>
                            )}
                            renderOption={() => null}
                        />
                </Box>
                </form>
            </Paper>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={handleCloseError}
            >
                <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SearchBar;
